
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import apiClient from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import RowActionsMenu from "../components/RowActionsMenu";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import ServiceDetailsModal from "./ServiceDetailsModal";

const { Text } = Typography;

const PERMISSIONS = {
  VIEW: "Gestion Commerciale.Services.view",
  CREATE: "Gestion Commerciale.Services.create",
  EDIT: "Gestion Commerciale.Services.edit",
  DELETE: "Gestion Commerciale.Services.delete",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const toPositiveIntOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toNonNegativeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

const normalizeWarehouseForPayload = (warehouseValue) => {
  if (!warehouseValue || warehouseValue === "__global__") return null;
  return toPositiveIntOrNull(warehouseValue);
};

const normalizeComponentsInput = (rawComponents) => {
  const normalized = [];
  let hasIncompleteRows = false;

  (Array.isArray(rawComponents) ? rawComponents : []).forEach((row, index) => {
    const hasAnyValue =
      row &&
      (row.product_id !== undefined ||
        row.quantity_per_service !== undefined ||
        row.wastage_percent !== undefined ||
        row.sort_order !== undefined);

    if (!hasAnyValue) return;

    const productId = toPositiveIntOrNull(row?.product_id);
    const quantityPerService = Number(row?.quantity_per_service);

    if (!productId || !Number.isFinite(quantityPerService) || quantityPerService <= 0) {
      hasIncompleteRows = true;
      return;
    }

    const wastagePercent = toNonNegativeNumber(row?.wastage_percent, 0);
    const sortOrderCandidate = Number(row?.sort_order);
    const sortOrder =
      Number.isInteger(sortOrderCandidate) && sortOrderCandidate >= 0
        ? sortOrderCandidate
        : index + 1;

    normalized.push({
      product_id: productId,
      quantity_per_service: quantityPerService,
      wastage_percent: wastagePercent,
      sort_order: sortOrder,
    });
  });

  return {
    rows: normalized,
    hasIncompleteRows,
  };
};

const isBillingEligibleProduct = (product) => {
  const productType = String(product?.product_type || "").toLowerCase();
  const productionKind = String(product?.production_kind || "finished").toLowerCase();
  if (productType === "raw") return false;
  if (productType === "production" && productionKind !== "finished") return false;
  return true;
};

const GestionServices = () => {
  const invalidateServices = useBusinessMutationInvalidation("service");
  const [services, setServices] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [detailSelection, setDetailSelection] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "active",
    warehouse: "all",
  });
  const [costPreview, setCostPreview] = useState(null);
  const [costPreviewLoading, setCostPreviewLoading] = useState(false);
  const [costPreviewError, setCostPreviewError] = useState(null);
  const [form] = Form.useForm();
  const previewRequestIdRef = useRef(0);

  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const hasPermission = useCallback(
    (permissionKey) => user?.permissions?.includes(permissionKey) || false,
    [user]
  );

  const canView = hasPermission(PERMISSIONS.VIEW);
  const canCreate = hasPermission(PERMISSIONS.CREATE);
  const canEdit = hasPermission(PERMISSIONS.EDIT);
  const canDelete = hasPermission(PERMISSIONS.DELETE);
  const detailOpen = Boolean(canView && detailSelection
    && String(detailSelection.companyId) === String(selectedCompany)
    && String(detailSelection.selectedWarehouseId || "") === String(selectedWarehouse || ""));

  useEffect(() => { setDetailSelection(null); }, [selectedCompany, selectedWarehouse]);

  const watchedAllValues = Form.useWatch([], form);
  const watchedWarehouseId = Form.useWatch("warehouse_id", form);
  const watchedBillingProductId = Form.useWatch("product_id", form);
  const watchedComponents = Form.useWatch("components", form) || [];

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      warehouse: selectedWarehouse ? String(selectedWarehouse) : "all",
    }));
  }, [selectedCompany, selectedWarehouse]);

  const fetchWarehouses = useCallback(async () => {
    if (!selectedCompany || !canView) {
      setWarehouses([]);
      return;
    }

    setWarehousesLoading(true);
    try {
      const response = await apiClient.get("/api/warehouses", {
        params: {
          company_id: selectedCompany,
          page: 1,
          limit: 500,
        },
      });
      const warehousesData = response.data?.warehouses || [];
      setWarehouses(Array.isArray(warehousesData) ? warehousesData : []);
    } catch (error) {
      console.error("[SERVICES] Error fetching warehouses:", error);
      message.error("Impossible de charger les établissements.");
      setWarehouses([]);
    } finally {
      setWarehousesLoading(false);
    }
  }, [selectedCompany, canView]);

  const fetchProducts = useCallback(async () => {
    if (!selectedCompany || !canView) {
      setProducts([]);
      return;
    }

    setProductsLoading(true);
    try {
      const response = await apiClient.get("/api/produits", {
        params: {
          company_id: selectedCompany,
          all: "true",
          fetch_all_limit: 3000,
        },
      });

      const productsData = response.data?.products || [];
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error("[SERVICES] Error fetching products:", error);
      message.error("Impossible de charger les produits.");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, [selectedCompany, canView]);

  const fetchServices = useCallback(async () => {
    if (!selectedCompany || !canView) {
      setServices([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        company_id: selectedCompany,
        status: filters.status,
        page: 1,
        limit: 200,
      };

      if (filters.search.trim()) {
        params.search = filters.search.trim();
      }
      if (filters.warehouse === "__global__") {
        params.warehouse_id = "null";
      } else if (filters.warehouse !== "all") {
        params.warehouse_id = filters.warehouse;
      }
      if (selectedWarehouse) {
        params.preview_warehouse_id = selectedWarehouse;
      }

      const response = await apiClient.get("/api/services", { params });
      setServices(response.data?.services || []);
    } catch (error) {
      console.error("[SERVICES] Error fetching services:", error);
      message.error("Impossible de charger les services.");
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedWarehouse, canView, filters]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!watchedWarehouseId || watchedWarehouseId === "__global__") return;
    form.setFieldValue("preview_warehouse_id", String(watchedWarehouseId));
  }, [modalOpen, watchedWarehouseId, form]);

  const warehouseOptions = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        value: String(warehouse.id),
        label: warehouse.name,
      })),
    [warehouses]
  );

  const productMapById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      map.set(Number(product.id), product);
    });
    return map;
  }, [products]);

  const billingProductOptions = useMemo(
    () =>
      products
        .filter(isBillingEligibleProduct)
        .map((product) => ({
          value: Number(product.id),
          label: product.name,
        })),
    [products]
  );

  const componentProductOptions = useMemo(
    () =>
      products.map((product) => ({
        value: Number(product.id),
        label: product.name,
      })),
    [products]
  );

  const previewLineByProductId = useMemo(() => {
    const map = new Map();
    (costPreview?.components_breakdown || []).forEach((line) => {
      map.set(Number(line.product_id), line);
    });
    return map;
  }, [costPreview]);
  const openCreateModal = () => {
    if (!selectedCompany) {
      message.warning("Selectionnez d'abord une entreprise.");
      return;
    }

    setEditingService(null);
    setCostPreview(null);
    setCostPreviewError(null);
    form.resetFields();
    form.setFieldsValue({
      warehouse_id: selectedWarehouse ? String(selectedWarehouse) : "__global__",
      preview_warehouse_id: selectedWarehouse ? String(selectedWarehouse) : undefined,
      product_id: undefined,
      code: "",
      name: "",
      description: "",
      duration_minutes: null,
      base_price: 0,
      labor_cost: 0,
      overhead_cost: 0,
      components: [],
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setCostPreview(service?.cost_preview || null);
    setCostPreviewError(null);
    form.resetFields();
    form.setFieldsValue({
      warehouse_id: service.warehouse_id ? String(service.warehouse_id) : "__global__",
      preview_warehouse_id: service.warehouse_id
        ? String(service.warehouse_id)
        : selectedWarehouse
        ? String(selectedWarehouse)
        : undefined,
      product_id: service.product_id ? Number(service.product_id) : undefined,
      code: service.code || "",
      name: service.name || "",
      description: service.description || "",
      duration_minutes: service.duration_minutes || null,
      base_price: Number(service.base_price || 0),
      labor_cost: Number(service.labor_cost || 0),
      overhead_cost: Number(service.overhead_cost || 0),
      components: Array.isArray(service.components)
        ? service.components.map((component, index) => ({
            product_id: Number(component.product_id),
            quantity_per_service: Number(component.quantity_per_service || 0),
            wastage_percent: Number(component.wastage_percent || 0),
            sort_order: Number(component.sort_order || index + 1),
          }))
        : [],
      is_active: Number(service.is_active) === 1,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingService(null);
    setCostPreview(null);
    setCostPreviewError(null);
    setCostPreviewLoading(false);
    form.resetFields();
  };

  const requestCostPreview = useCallback(
    async (values) => {
      if (!selectedCompany || !modalOpen) return;

      const basePriceRaw = Number(values?.base_price);
      if (!Number.isFinite(basePriceRaw) || basePriceRaw < 0) {
        setCostPreview(null);
        setCostPreviewError("Le tarif de base doit etre un nombre positif ou nul.");
        return;
      }

      const { rows: normalizedComponents, hasIncompleteRows } = normalizeComponentsInput(
        values?.components
      );
      if (hasIncompleteRows) {
        setCostPreview(null);
        setCostPreviewError(
          "Completez chaque composant (produit et quantite) avant de continuer."
        );
        return;
      }

      const warehouseId = normalizeWarehouseForPayload(values?.warehouse_id);
      const previewWarehouseField = toPositiveIntOrNull(values?.preview_warehouse_id);
      const fallbackWarehouseId = toPositiveIntOrNull(selectedWarehouse);
      const previewWarehouseId = previewWarehouseField || warehouseId || fallbackWarehouseId;

      if (normalizedComponents.length > 0 && !previewWarehouseId) {
        setCostPreview(null);
        setCostPreviewError(
          "Ajoutez un établissement de simulation pour calculer le cout des composants."
        );
        return;
      }

      const requestId = previewRequestIdRef.current + 1;
      previewRequestIdRef.current = requestId;
      setCostPreviewLoading(true);
      setCostPreviewError(null);

      try {
        const payload = {
          company_id: selectedCompany,
          service_id: editingService ? Number(editingService.id) : undefined,
          warehouse_id: warehouseId,
          preview_warehouse_id: previewWarehouseId || undefined,
          product_id: values?.product_id ? Number(values.product_id) : null,
          base_price: toNonNegativeNumber(values?.base_price, 0),
          labor_cost: toNonNegativeNumber(values?.labor_cost, 0),
          overhead_cost: toNonNegativeNumber(values?.overhead_cost, 0),
          components: normalizedComponents,
        };

        const response = await apiClient.post("/api/services/cost-preview", payload);
        if (requestId !== previewRequestIdRef.current) return;
        setCostPreview(response.data?.cost_preview || null);
        setCostPreviewError(null);
      } catch (error) {
        if (requestId !== previewRequestIdRef.current) return;
        const backendMessage =
          error.response?.data?.error ||
          "Impossible de calculer le cout du service pour le moment.";
        setCostPreview(null);
        setCostPreviewError(backendMessage);
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setCostPreviewLoading(false);
        }
      }
    },
    [editingService, modalOpen, selectedCompany, selectedWarehouse]
  );

  useEffect(() => {
    if (!modalOpen) return undefined;
    const timeout = setTimeout(() => {
      const currentValues = watchedAllValues || form.getFieldsValue(true);
      requestCostPreview(currentValues);
    }, 300);
    return () => clearTimeout(timeout);
  }, [modalOpen, watchedAllValues, form, requestCostPreview]);

  const handleSubmit = async (values) => {
    if (!selectedCompany) {
      message.error("Aucune entreprise selectionnee.");
      return;
    }

    const { rows: normalizedComponents, hasIncompleteRows } = normalizeComponentsInput(
      values.components
    );
    if (hasIncompleteRows) {
      message.error("Chaque composant doit avoir un produit et une quantite.");
      return;
    }

    if (costPreviewError) {
      message.error(costPreviewError);
      return;
    }

    if (costPreview?.blocking_reasons?.length > 0) {
      message.error(costPreview.blocking_reasons[0]);
      return;
    }

    if (costPreview && Number(costPreview.pricing_gap || 0) < 0) {
      message.error("Le tarif du service est inferieur au cout calcule.");
      return;
    }

    const warehouseId = normalizeWarehouseForPayload(values.warehouse_id);
    const previewWarehouseField = toPositiveIntOrNull(values.preview_warehouse_id);
    const fallbackWarehouseId = toPositiveIntOrNull(selectedWarehouse);
    const previewWarehouseId = previewWarehouseField || warehouseId || fallbackWarehouseId;

    const payload = {
      company_id: selectedCompany,
      warehouse_id: warehouseId,
      preview_warehouse_id: previewWarehouseId || undefined,
      product_id: values.product_id ? Number(values.product_id) : null,
      code: values.code?.trim() || null,
      name: values.name?.trim(),
      description: values.description?.trim() || null,
      duration_minutes: values.duration_minutes || null,
      base_price: toNonNegativeNumber(values.base_price, 0),
      labor_cost: toNonNegativeNumber(values.labor_cost, 0),
      overhead_cost: toNonNegativeNumber(values.overhead_cost, 0),
      components: normalizedComponents,
      is_active: values.is_active ? 1 : 0,
    };

    setSubmitting(true);
    try {
      if (editingService) {
        await apiClient.put(`/api/services/${editingService.id}`, payload);
        message.success("Service mis a jour avec succes.");
      } else {
        await apiClient.post("/api/services", payload);
        message.success("Service cree avec succes.");
      }
      void invalidateServices();
      closeModal();
      fetchServices();
    } catch (error) {
      const backendMessage = error.response?.data?.error;
      message.error(backendMessage || "Erreur lors de la sauvegarde du service.");
      console.error("[SERVICES] Error saving service:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (service) => {
    Modal.confirm({
      title: "Supprimer ce service ?",
      content:
        "Cette action est definitive. Verifiez qu'il n'est plus utilise avant suppression.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      centered: true,
      onOk: async () => {
        try {
          await apiClient.delete(`/api/services/${service.id}`);
          void invalidateServices();
          message.success("Service supprime avec succes.");
          fetchServices();
        } catch (error) {
          const backendMessage = error.response?.data?.error;
          message.error(backendMessage || "Erreur lors de la suppression du service.");
          console.error("[SERVICES] Error deleting service:", error);
        }
      },
    });
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => String(a.name || "").localeCompare(String(b.name || "")),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (value) => value || <Text type="secondary">-</Text>,
    },
    {
      title: "Tarif",
      dataIndex: "base_price",
      key: "base_price",
      render: (value) => `${formatCurrency(value)} CFA`,
    },
    {
      title: "Cout estime",
      key: "service_cost_total",
      render: (_, record) => {
        const totalCost = Number(record?.cost_preview?.service_cost_total || 0);
        return `${formatCurrency(totalCost)} CFA`;
      },
    },
    {
      title: "Marge estimee",
      key: "pricing_gap",
      render: (_, record) => {
        const pricingGap = Number(record?.cost_preview?.pricing_gap || 0);
        if (pricingGap < 0) {
          return <Tag color="red">{formatCurrency(pricingGap)} CFA</Tag>;
        }
        return <Tag color="green">{formatCurrency(pricingGap)} CFA</Tag>;
      },
    },
    {
      title: "Composants",
      key: "components_count",
      render: (_, record) => Number(record?.components?.length || 0),
    },
    {
      title: "Produit facture",
      dataIndex: "product_name",
      key: "product_name",
      render: (value) => value || <Text type="secondary">Non defini</Text>,
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      render: (value) => value || <Text type="secondary">Global entreprise</Text>,
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value) =>
        Number(value) === 1 ? <Tag color="green">Actif</Tag> : <Tag color="red">Inactif</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 112,
      align: "center",
      render: (_, record) => {
        const actionItems = [];

        if (canEdit) {
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: "Modifier",
            onClick: () => openEditModal(record),
          });
        }

        if (canDelete) {
          if (actionItems.length > 0) actionItems.push({ type: "divider" });
          actionItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => handleDelete(record),
          });
        }

        return (
          <span className="service-details-actions">
            <Button type="text" icon={<EyeOutlined />} title="Voir les détails"
              aria-label={`Voir les détails du service ${record.name || record.id}`}
              onClick={() => setDetailSelection({ id: record.id, companyId: selectedCompany,
                warehouseId: record.warehouse_id || selectedWarehouse || null,
                selectedWarehouseId: selectedWarehouse })} />
            <RowActionsMenu
              items={actionItems}
              ariaLabel={`Afficher les actions du service ${record.name || record.id}`}
            />
          </span>
        );
      },
    },
  ];

  const hasBlockingReasons = (costPreview?.blocking_reasons || []).length > 0;
  const hasNegativePricingGap = Number(costPreview?.pricing_gap || 0) < 0;
  const disableSave =
    submitting ||
    costPreviewLoading ||
    Boolean(costPreviewError) ||
    hasBlockingReasons ||
    hasNegativePricingGap;

  if (!canView) {
    return (
      <div className="p-sm md:p-md lg:p-lg min-h-screen">
        <Alert
          type="warning"
          showIcon
          message="Acces refuse"
          description="Vous n'avez pas la permission d'afficher le sous-module Services."
        />
      </div>
    );
  }

  if (!selectedCompany) {
    return (
      <div className="p-sm md:p-md lg:p-lg min-h-screen">
        <Alert
          type="warning"
          showIcon
          message="Aucune entreprise selectionnee"
          description="Selectionnez une entreprise pour gerer les services."
        />
      </div>
    );
  }

  return (
    <div className="p-sm md:p-md lg:p-lg min-h-screen">
      <div
        className="mb-lg p-md shadow rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center">
            <ToolOutlined className="mr-3" /> Gestion des Services
          </h1>
          <p className="mt-1 text-xs sm:text-sm" style={{ color: "var(--text-secondary)" }}>
            Definissez les services, composants consommables, et le cout reel avant facturation.
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Ajouter un service
          </Button>
        )}
      </div>

      <div
        className="mb-md p-md shadow rounded-lg flex flex-col md:flex-row gap-3"
        style={{ backgroundColor: "var(--bg-primary)" }}
      >
        <Input
          allowClear
          value={filters.search}
          placeholder="Rechercher par nom ou code..."
          prefix={<SearchOutlined />}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              search: e.target.value,
            }))
          }
        />
        <Select
          value={filters.status}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          style={{ minWidth: 180 }}
          options={[
            { value: "active", label: "Actifs" },
            { value: "inactive", label: "Inactifs" },
            { value: "all", label: "Tous" },
          ]}
        />
        <Select
          value={filters.warehouse}
          loading={warehousesLoading}
          onChange={(value) => setFilters((prev) => ({ ...prev, warehouse: value }))}
          style={{ minWidth: 260 }}
          options={[
            { value: "all", label: "Tous les établissements" },
            { value: "__global__", label: "Global entreprise" },
            ...warehouseOptions,
          ]}
        />
      </div>

      <div className="shadow rounded-lg" style={{ backgroundColor: "var(--bg-primary)" }}>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={services}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 1400 }}
          locale={{
            emptyText: "Aucun service trouve pour ces filtres.",
          }}
        />
      </div>

      <ServiceDetailsModal open={detailOpen} serviceId={detailSelection?.id} userId={user?.id}
        companyId={selectedCompany} warehouseId={detailSelection?.warehouseId}
        previewWarehouseId={selectedWarehouse}
        previewWarehouseName={warehouses.find((warehouse) => String(warehouse.id) === String(selectedWarehouse))?.name}
        canEdit={canEdit} onClose={() => setDetailSelection(null)}
        onEdit={(service) => { if (canEdit && detailOpen) { setDetailSelection(null); openEditModal(service); } }} />

      <Modal
        title={editingService ? "Modifier un service" : "Nouveau service"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okButtonProps={{ loading: submitting, disabled: disableSave }}
        okText={editingService ? "Mettre a jour" : "Creer"}
        cancelText="Annuler"
        destroyOnClose
        width={1150}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ is_active: true }}>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label="Établissement" name="warehouse_id">
                <Select
                  loading={warehousesLoading}
                  options={[
                    { value: "__global__", label: "Global entreprise" },
                    ...warehouseOptions,
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Établissement simulation cout" name="preview_warehouse_id">
                <Select
                  allowClear
                  showSearch
                  loading={warehousesLoading}
                  options={warehouseOptions}
                  optionFilterProp="label"
                  placeholder="Établissement utilise pour le calcul"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Produit associé (optionnel)"
                name="product_id"
                extra="Lien de compatibilité uniquement : le service peut être vendu directement sans produit."
              >
                <Select
                  showSearch
                  allowClear
                  loading={productsLoading}
                  options={billingProductOptions}
                  optionFilterProp="label"
                  placeholder="Aucun produit requis"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Nom du service"
                name="name"
                rules={[{ required: true, message: "Le nom du service est requis." }]}
              >
                <Input maxLength={191} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Code interne" name="code">
                <Input maxLength={64} placeholder="Ex: SVC-SOIN-VISAGE" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Duree (minutes)" name="duration_minutes">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Tarif de base (CFA)"
                name="base_price"
                rules={[
                  { required: true, message: "Le tarif de base est requis." },
                  {
                    type: "number",
                    min: 0,
                    message: "Le tarif doit etre positif ou nul.",
                  },
                ]}
              >
                <InputNumber min={0} precision={2} step={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Main d'oeuvre (CFA)"
                name="labor_cost"
                rules={[
                  {
                    type: "number",
                    min: 0,
                    message: "Le cout de main d'oeuvre doit etre positif ou nul.",
                  },
                ]}
              >
                <InputNumber min={0} precision={2} step={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Charges fixes (CFA)"
                name="overhead_cost"
                rules={[
                  {
                    type: "number",
                    min: 0,
                    message: "Le cout de charges doit etre positif ou nul.",
                  },
                ]}
              >
                <InputNumber min={0} precision={2} step={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} maxLength={5000} />
          </Form.Item>

          <Divider orientation="left">Composants consommables</Divider>

          <Form.List name="components">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {fields.map((field, index) => {
                  const rowValue = watchedComponents[field.name] || {};
                  const previewLine = previewLineByProductId.get(
                    Number(rowValue?.product_id || 0)
                  );
                  const unitCost = previewLine?.unit_cost;
                  const lineCost = previewLine?.line_cost;
                  const productMeta = productMapById.get(Number(rowValue?.product_id || 0));

                  return (
                    <Card key={field.key} size="small">
                      <Row gutter={8} align="middle">
                        <Col xs={24} md={8}>
                          <Form.Item
                            {...field}
                            label={index === 0 ? "Produit" : "Produit"}
                            name={[field.name, "product_id"]}
                            rules={[
                              { required: true, message: "Produit requis." },
                              () => ({
                                validator(_, value) {
                                  if (!value) return Promise.resolve();
                                  if (
                                    watchedBillingProductId &&
                                    Number(value) === Number(watchedBillingProductId)
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        "Le produit de facturation ne peut pas etre un composant."
                                      )
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <Select
                              showSearch
                              allowClear
                              loading={productsLoading}
                              options={componentProductOptions}
                              optionFilterProp="label"
                              placeholder="Produit consomme"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                          <Form.Item
                            {...field}
                            label="Qte / service"
                            name={[field.name, "quantity_per_service"]}
                            rules={[
                              { required: true, message: "Qte requise." },
                              {
                                type: "number",
                                min: 0.0001,
                                message: "Qte > 0",
                              },
                            ]}
                          >
                            <InputNumber
                              min={0.0001}
                              precision={4}
                              step={0.1}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                          <Form.Item
                            {...field}
                            label="Perte %"
                            name={[field.name, "wastage_percent"]}
                            initialValue={0}
                            rules={[
                              {
                                type: "number",
                                min: 0,
                                message: "Valeur >= 0",
                              },
                            ]}
                          >
                            <InputNumber min={0} precision={2} step={0.5} style={{ width: "100%" }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={3}>
                          <Text type="secondary">Cout unitaire</Text>
                          <div style={{ minHeight: 32, lineHeight: "32px" }}>
                            {unitCost === null || unitCost === undefined
                              ? "-"
                              : `${formatCurrency(unitCost)} CFA`}
                          </div>
                        </Col>
                        <Col xs={24} md={3}>
                          <Text type="secondary">Cout ligne</Text>
                          <div style={{ minHeight: 32, lineHeight: "32px" }}>
                            {lineCost === null || lineCost === undefined
                              ? "-"
                              : `${formatCurrency(lineCost)} CFA`}
                          </div>
                        </Col>
                        <Col xs={24} md={2}>
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                            style={{ marginTop: 24, width: "100%" }}
                          />
                        </Col>
                      </Row>
                      {productMeta?.stock_management_mode && (
                        <Text type="secondary">
                          Mode stock: {String(productMeta.stock_management_mode)}
                        </Text>
                      )}
                    </Card>
                  );
                })}

                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      product_id: undefined,
                      quantity_per_service: 1,
                      wastage_percent: 0,
                      sort_order: fields.length + 1,
                    })
                  }
                  block
                >
                  Ajouter un composant
                </Button>
              </Space>
            )}
          </Form.List>

          <Divider orientation="left">Cout temps reel</Divider>

          {costPreviewLoading && (
            <Alert type="info" showIcon message="Calcul du cout en cours..." className="mb-2" />
          )}
          {costPreviewError && (
            <Alert type="error" showIcon message={costPreviewError} className="mb-2" />
          )}

          {costPreview && (
            <Card size="small">
              <Row gutter={12}>
                <Col xs={24} md={6}>
                  <Text type="secondary">Cout consommables</Text>
                  <div>{formatCurrency(costPreview.consumables_cost_total)} CFA</div>
                </Col>
                <Col xs={24} md={6}>
                  <Text type="secondary">Main d'oeuvre</Text>
                  <div>{formatCurrency(costPreview.labor_cost)} CFA</div>
                </Col>
                <Col xs={24} md={6}>
                  <Text type="secondary">Charges fixes</Text>
                  <div>{formatCurrency(costPreview.overhead_cost)} CFA</div>
                </Col>
                <Col xs={24} md={6}>
                  <Text strong>Cout total service</Text>
                  <div>{formatCurrency(costPreview.service_cost_total)} CFA</div>
                </Col>
              </Row>
              <Divider style={{ margin: "12px 0" }} />
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Text type="secondary">Tarif actuel</Text>
                  <div>{formatCurrency(costPreview.base_price)} CFA</div>
                </Col>
                <Col xs={24} md={8}>
                  <Text type="secondary">Marge</Text>
                  <div>
                    {Number(costPreview.pricing_gap || 0) < 0 ? (
                      <Tag color="red">{formatCurrency(costPreview.pricing_gap)} CFA</Tag>
                    ) : (
                      <Tag color="green">{formatCurrency(costPreview.pricing_gap)} CFA</Tag>
                    )}
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <Text type="secondary">Regle tarifaire</Text>
                  <div>
                    {costPreview.pricing_ok ? (
                      <Tag color="green">OK</Tag>
                    ) : (
                      <Tag color="red">Bloque</Tag>
                    )}
                  </div>
                </Col>
              </Row>

              {Array.isArray(costPreview.blocking_reasons) &&
                costPreview.blocking_reasons.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    className="mt-2"
                    message={costPreview.blocking_reasons.join(" ")}
                  />
                )}
            </Card>
          )}

          <Form.Item label="Actif" name="is_active" valuePropName="checked" style={{ marginTop: 16 }}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GestionServices;
